import { useState, useMemo } from 'react'
import SubHeaders from '@/components/Headers/SubHeaders'
import TextEditorCustomize from '@/components/TextEditorCustomize'
import { paths } from '@/utils/path'
import { Controller, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Button, FormItem, Loading, Tabs, Typogaphy } from 'vnetwork-ui'
import { Settings, History } from 'lucide-react'
import { useQuickHelp, QuickHelpOption } from '@/contexts/quick-help'

import {
    VariableMapping,
    SmtpSettings,
    ActionBar,
    ReceiversSection,
    SenderSettings,
} from './components'
import { useCampaignForm, useVariableManagement } from './hooks'
import EyeIcons from '@/icons/EyeIcons'
import EditIcons from '@/icons/EditIcons'
import SendIcons from '@/icons/SendIcons'
import DownLoadIcons from '@/icons/DownLoadIcons'
import { formatFileSize } from '@/utils/function'
import DeliveryProgress from '../shared/DeliveryProgress'
import useTabs from '@/hooks/useTabs'

// Helper to detect if content is full HTML document
const isFullHtmlDocument = (html: string): boolean => {
    if (!html) return false
    const trimmed = html.trim().toLowerCase()
    return (
        trimmed.startsWith('<!doctype') ||
        trimmed.startsWith('<html') ||
        (trimmed.includes('<head') && trimmed.includes('<body'))
    )
}

const Detail: React.FC = () => {
    const { t } = useTranslation()
    const { id } = useParams<{ id: string }>()
    const campaignId = id ? Number(id) : null

    const {
        formMethods,
        watchedValues,
        isNew,
        isLoadingCampaign,
        campaign,
        receivers,
        importedColumns,
        selectedTemplateVariables,
        templateOptions,
        currentUserEmail,
        isCreating,
        isUpdating,
        isSendingTest,
        isStartingDelivery,
        savedCustomerVariables,
        savedSharedVariables,
        attachments,
        setAttachments,
        selectedSource,
        handleSourceChange,
        // Internal mode and filters
        filtersPreview,
        isPreviewingFilters,
        filtersPage,
        filtersPageSize,
        filtersTotalWithoutSearch,
        handleInternalModeChange,
        handlePreviewFilters,
        handleFiltersPageChange,
        handleToggleExcludeCustomer,
        handleReceiversChange,
        handleTemplateChange,
        onSubmit,
        handleSendTest,
        handleBulkDelivery,
        navigate,
    } = useCampaignForm(campaignId)

    // Extract control and handleSubmit from formMethods for proper type inference
    const { control, handleSubmit } = formMethods

    // Quick Help options for Campaign Detail page
    const quickHelpOptions: QuickHelpOption[] = useMemo(
        () => [
            {
                id: 'campaign-name',
                label: 'Đặt tên Campaign',
                description: 'Tên để nhận diện chiến dịch',
                selector: '[name="mailer_campaign_name"]',
                tourTitle: 'Tên Campaign',
                tourDescription:
                    'Đặt tên cho chiến dịch email. Tên này giúp bạn nhận diện campaign trong danh sách.',
            },
            {
                id: 'select-template',
                label: 'Chọn Template Email',
                description: 'Chọn mẫu email đã tạo',
                selector: '[name="mailer_template_id"]',
                tourTitle: 'Chọn Template',
                tourDescription:
                    'Chọn template email đã tạo sẵn. Nội dung và biến của template sẽ được áp dụng cho campaign.',
            },
            {
                id: 'email-content',
                label: 'Xem/Sửa nội dung email',
                description: 'Chỉnh sửa nội dung trước khi gửi',
                selector: '.text-editor-container',
                tourTitle: 'Nội Dung Email',
                tourDescription:
                    'Xem và chỉnh sửa nội dung email. Nhấn "Preview" để xem với dữ liệu thực, "Edit" để chỉnh sửa.',
            },
            {
                id: 'test-email',
                label: 'Gửi Email Test',
                description: 'Test trước khi gửi hàng loạt',
                buttonLabel: 'Test',
                tourTitle: 'Gửi Test Email',
                tourDescription:
                    'Nhập email của bạn và nhấn Test để nhận email mẫu. Kiểm tra nội dung, format trước khi gửi hàng loạt.',
            },
            {
                id: 'receivers-section',
                label: 'Chọn người nhận',
                description: 'Internal (từ hệ thống) hoặc External (import)',
                selector: '[data-tour="receivers-section"]',
                tourTitle: 'Danh Sách Người Nhận',
                tourDescription:
                    'Chọn nguồn người nhận: Internal (lọc từ database khách hàng) hoặc External (import file Excel/CSV).',
            },
            {
                id: 'smtp-settings',
                label: 'Cấu hình SMTP',
                description: 'Tùy chọn SMTP riêng',
                selector: '[data-tour="smtp-settings"]',
                tourTitle: 'Cấu Hình SMTP',
                tourDescription:
                    'Bật để sử dụng SMTP server riêng thay vì server mặc định. Nhập thông tin server và test kết nối.',
            },
            {
                id: 'save-campaign',
                label: 'Lưu Campaign',
                description: 'Lưu thông tin campaign',
                buttonLabel: t('save'),
                tourTitle: 'Lưu Campaign',
                tourDescription:
                    'Lưu campaign để có thể gửi test hoặc tiếp tục chỉnh sửa sau.',
            },
            {
                id: 'start-delivery',
                label: 'Bắt đầu gửi',
                description: 'Gửi email hàng loạt',
                buttonLabel: t('mailerCampaign:start_delivery'),
                tourTitle: 'Bắt Đầu Gửi',
                tourDescription:
                    'Nhấn để bắt đầu gửi email đến tất cả người nhận. Theo dõi tiến độ trong tab "Lịch sử gửi".',
            },
        ],
        [t]
    )

    useQuickHelp(quickHelpOptions)

    // Check if internal + filters mode
    const isInternalFiltersMode =
        watchedValues.mailer_campaign_source_type === 'internal' &&
        watchedValues.mailer_campaign_internal_mode === 'filters'

    const {
        activeVariables,
        receiverVariables,
        sharedVariables,
        sharedVariablesWithValues,
        editorTemplateVariables,
        customerVariableNames,
        sharedVariableValues,
        draggedVariable,
        moveToCustomer,
        moveToShared,
        handleDragStart,
        handleDragOver,
        handleDropToCustomer,
        handleDropToShared,
        handleSharedValueChange,
        handleEditorVariablesChange,
        replaceVariables,
    } = useVariableManagement(
        watchedValues.template_content,
        selectedTemplateVariables,
        {
            initialCustomerVariables: savedCustomerVariables,
            initialSharedVariables: savedSharedVariables,
            // For internal + filters: always show receiver vars, only show shared vars if in content
            alwaysShowReceiverVariables: isInternalFiltersMode,
        }
    )

    // For existing campaigns, start in preview mode; for new campaigns, start in edit mode
    const [isEditingContent, setIsEditingContent] = useState(isNew)
    const [smtpTested, setSmtpTested] = useState(false)

    // Check if campaign has been sent (show history tab)
    const showHistoryTab = !isNew

    // Tabs configuration - only show history tab when campaign has been sent
    const tabs = useMemo(() => {
        const baseTabs = [
            {
                label: t('mailerCampaign:campaign_config'),
                value: 'config',
                startIcon: <Settings size={16} className="text-primary" />,
                component: <></>, // Will be rendered manually below
            },
        ]
        if (showHistoryTab) {
            baseTabs.push({
                label: t('mailerCampaign:delivery_history'),
                value: 'history',
                startIcon: <History size={16} className="text-primary" />,
                component: <></>, // Will be rendered manually below
            })
        }
        return baseTabs
    }, [t, showHistoryTab])

    const { TabProps } = useTabs({ tabs })
    const activeTab = TabProps.activeTab || 'config'
    const setActiveTab = TabProps.onChangeTab

    // Combined variable values for TextEditor preview
    const editorVariableValues = useMemo(() => {
        const values: Record<string, string> = { ...sharedVariableValues }
        // Add receiver values from first receiver for preview
        const firstReceiver = receivers[0] || {}
        receiverVariables.forEach((v) => {
            if (firstReceiver[v.name]) {
                values[v.name] = firstReceiver[v.name] as string
            }
        })
        // Add email if exists
        if (firstReceiver.email) {
            values['email'] = firstReceiver.email
        }
        return values
    }, [sharedVariableValues, receivers, receiverVariables])

    // Download EML file
    const handleDownloadEml = () => {
        // Helper to encode UTF-8 string to base64
        const utf8ToBase64 = (str: string) => {
            const encoder = new TextEncoder()
            const bytes = encoder.encode(str)
            let binary = ''
            bytes.forEach((byte) => (binary += String.fromCharCode(byte)))
            return btoa(binary)
        }

        const subject = replaceVariables(
            watchedValues.template_subject || '',
            receivers,
            0
        )
        // Strip HTML tags for plain text, keep HTML for html part
        const htmlContent = replaceVariables(
            watchedValues.template_content || '',
            receivers,
            0
        )
        const textContent = htmlContent.replace(/<[^>]*>/g, '')

        const boundary = '----=_Part_' + Date.now()
        const toEmail =
            watchedValues.test_email ||
            currentUserEmail ||
            'recipient@example.com'

        const emlContent = [
            `MIME-Version: 1.0`,
            `From: ${watchedValues.metadata?.mailer?.user_name || 'no-reply@vnetwork.vn'}`,
            `To: ${toEmail}`,
            `Subject: =?UTF-8?B?${utf8ToBase64(subject)}?=`,
            `Content-Type: multipart/alternative; boundary="${boundary}"`,
            ``,
            `--${boundary}`,
            `Content-Type: text/plain; charset=UTF-8`,
            `Content-Transfer-Encoding: base64`,
            ``,
            utf8ToBase64(textContent),
            ``,
            `--${boundary}`,
            `Content-Type: text/html; charset=UTF-8`,
            `Content-Transfer-Encoding: base64`,
            ``,
            utf8ToBase64(htmlContent),
            ``,
            `--${boundary}--`,
        ].join('\r\n')

        const blob = new Blob([emlContent], { type: 'message/rfc822' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${campaign?.mailer_campaign_name || 'email'}_preview.eml`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    if (isLoadingCampaign) {
        return (
            <div className="flex items-center justify-center h-[400px]">
                <Loading.CircularProgress width={40} height={40} />
            </div>
        )
    }

    return (
        <div className="space-y-2">
            <SubHeaders
                breadcrumbs={[
                    {
                        path: paths.mailerCampaign,
                        title: t('mailerCampaign:mailers'),
                    },
                    {
                        path: '#',
                        title: isNew
                            ? t('mailerCampaign:create_campaign')
                            : campaign?.mailer_campaign_name || '',
                    },
                ]}
            />

            {/* Tabs - only show for existing campaigns that have been sent */}
            {showHistoryTab && <Tabs {...TabProps} />}

            {/* Tab Content */}
            {activeTab === 'config' ? (
                <div className="space-y-2 bg-white rounded-md p-4 shadow-sm border">
                    <FormProvider {...formMethods}>
                        <form
                            onSubmit={handleSubmit((data) =>
                                onSubmit(
                                    data,
                                    {
                                        customerVariables: receiverVariables,
                                        sharedVariables:
                                            sharedVariablesWithValues,
                                    },
                                    attachments
                                )
                            )}
                            className="space-y-2"
                        >
                            {/* Basic Information */}
                            <Typogaphy className="font-medium text-primary capitalize mb-2">
                                {t('mailerCampaign:campaign_information')}
                            </Typogaphy>
                            <div className="grid grid-cols-1 gap-4">
                                <FormItem
                                    control={control}
                                    name="mailer_campaign_name"
                                    type="textfield"
                                    label="Campaign Name *"
                                    placeholder={t(
                                        'mailerCampaign:enter_campaign_name'
                                    )}
                                />
                                <FormItem
                                    control={control}
                                    name="mailer_campaign_description"
                                    type="textfield"
                                    label={t('description')}
                                    placeholder={t(
                                        'mailerCampaign:enter_description'
                                    )}
                                />
                                <FormItem
                                    control={control}
                                    name="mailer_template_id"
                                    type="select"
                                    label="Campaign Email Template *"
                                    placeholder={t(
                                        'mailerCampaign:email_template'
                                    )}
                                    options={templateOptions}
                                    onChangeAfterHookForm={(
                                        value: number | null
                                    ) => {
                                        handleTemplateChange(value)
                                    }}
                                    disabled={!isNew}
                                />
                            </div>

                            {/* Email Content */}
                            <div className="p-4 border rounded-md border-primary relative !mt-4 space-y-2">
                                <div className="flex items-center absolute top-0 left-2 right-2 -translate-y-1/2">
                                    <Typogaphy className="font-medium text-sm capitalize bg-white px-2">
                                        Email Content
                                    </Typogaphy>
                                    {!isNew && (
                                        <div
                                            className="bg-white px-2 py-0.5 h-auto min-w-0 flex items-center gap-1 text-primary cursor-pointer"
                                            onClick={() =>
                                                setIsEditingContent(
                                                    !isEditingContent
                                                )
                                            }
                                        >
                                            {isEditingContent ? (
                                                <EyeIcons className="w-3 h-3 text-primary" />
                                            ) : (
                                                <EditIcons className="w-3 h-3 text-primary" />
                                            )}
                                            <Typogaphy className="text-sm">
                                                {isEditingContent
                                                    ? t('preview')
                                                    : t('edit')}
                                            </Typogaphy>
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    {isEditingContent ? (
                                        <>
                                            <FormItem
                                                control={control}
                                                name="template_subject"
                                                type="textfield"
                                                label={t(
                                                    'mailerCampaign:email_subject'
                                                )}
                                                placeholder={t(
                                                    'mailerCampaign:enter_email_subject'
                                                )}
                                            />
                                            <Controller
                                                name="template_content"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextEditorCustomize
                                                        key={`editor-${watchedValues.mailer_template_id}-${editorTemplateVariables.length}`}
                                                        value={{
                                                            text: field.value,
                                                            attachments:
                                                                attachments.map(
                                                                    (a) => ({
                                                                        file: new File(
                                                                            [],
                                                                            a.attachment_file_name
                                                                        ),
                                                                        url: `${import.meta.env.APP_STORAGE}/${a.attachment_file_url}`,
                                                                    })
                                                                ),
                                                        }}
                                                        onChange={(val) => {
                                                            field.onChange(
                                                                val.text
                                                            )
                                                            // Update attachments - filter only uploaded ones (have URL)
                                                            const uploadedAttachments =
                                                                val.attachments
                                                                    ?.filter(
                                                                        (a) =>
                                                                            a.url &&
                                                                            !a.uploading
                                                                    )
                                                                    .map(
                                                                        (
                                                                            a
                                                                        ) => ({
                                                                            attachment_file_url:
                                                                                a.url?.replace(
                                                                                    `${import.meta.env.APP_STORAGE}/`,
                                                                                    ''
                                                                                ) ||
                                                                                '',
                                                                            attachment_file_name:
                                                                                a
                                                                                    .file
                                                                                    .name,
                                                                            attachment_file_size:
                                                                                a
                                                                                    .file
                                                                                    .size,
                                                                            attachment_file_type:
                                                                                a
                                                                                    .file
                                                                                    .type,
                                                                        })
                                                                    ) || []
                                                            setAttachments(
                                                                uploadedAttachments
                                                            )
                                                        }}
                                                        placeholder={t(
                                                            'mailerCampaign:enter_email_content'
                                                        )}
                                                        enableVariables
                                                        templateVariables={
                                                            editorTemplateVariables
                                                        }
                                                        allowCustomVariables
                                                        onVariablesChange={
                                                            handleEditorVariablesChange
                                                        }
                                                        variableValues={
                                                            editorVariableValues
                                                        }
                                                        disabledAttachments={
                                                            false
                                                        }
                                                        attachmentPrefix="mailer/attachments"
                                                        imagePrefix="mailer/images"
                                                    />
                                                )}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            {/* Preview Only Mode - Replace variables with values */}
                                            <div className="space-y-2">
                                                <div>
                                                    <Typogaphy className="text-sm text-gray-500 mb-1">
                                                        {t(
                                                            'mailerCampaign:email_subject'
                                                        )}
                                                    </Typogaphy>
                                                    <Typogaphy className="font-medium">
                                                        {replaceVariables(
                                                            watchedValues.template_subject ||
                                                                '-',
                                                            receivers,
                                                            0
                                                        )}
                                                    </Typogaphy>
                                                </div>
                                                <div>
                                                    <Typogaphy className="text-sm text-gray-500 mb-1">
                                                        {t(
                                                            'mailerCampaign:email_content'
                                                        )}
                                                    </Typogaphy>
                                                    {watchedValues.metadata
                                                        ?.disable_layout ||
                                                    isFullHtmlDocument(
                                                        watchedValues.template_content ||
                                                            ''
                                                    ) ? (
                                                        <iframe
                                                            srcDoc={replaceVariables(
                                                                watchedValues.template_content ||
                                                                    '',
                                                                receivers,
                                                                0
                                                            )}
                                                            className="w-full min-h-[400px] border rounded-md bg-white"
                                                            title="Email Preview"
                                                        />
                                                    ) : (
                                                        <div
                                                            className="border rounded-md p-3 bg-gray-50 prose prose-sm max-w-none"
                                                            dangerouslySetInnerHTML={{
                                                                __html: replaceVariables(
                                                                    watchedValues.template_content ||
                                                                        '-',
                                                                    receivers,
                                                                    0
                                                                ),
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                {attachments.length > 0 && (
                                                    <div>
                                                        <Typogaphy className="text-sm text-gray-500 mb-1">
                                                            {t(
                                                                'mailerCampaign:attachments'
                                                            )}
                                                        </Typogaphy>
                                                        <div className="flex flex-col gap-1">
                                                            {attachments.map(
                                                                (a, i) => (
                                                                    <a
                                                                        key={i}
                                                                        href={`${import.meta.env.APP_STORAGE}/${a.attachment_file_url}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="flex items-center gap-2 text-sm text-primary hover:underline border rounded-md px-3 py-1.5 bg-gray-50 w-fit max-w-full"
                                                                    >
                                                                        <DownLoadIcons className="w-3 h-3 shrink-0" />
                                                                        <span className="truncate">
                                                                            {
                                                                                a.attachment_file_name
                                                                            }
                                                                        </span>
                                                                        <span className="shrink-0 text-xs text-gray-400">
                                                                            {formatFileSize(
                                                                                a.attachment_file_size
                                                                            )}
                                                                        </span>
                                                                    </a>
                                                                )
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                                {/* Variable Mapping */}
                                <VariableMapping
                                    activeVariables={activeVariables}
                                    sharedVariables={sharedVariables}
                                    receiverVariables={receiverVariables}
                                    customerVariableNames={
                                        customerVariableNames
                                    }
                                    sharedVariableValues={sharedVariableValues}
                                    importedColumns={importedColumns}
                                    receivers={receivers}
                                    draggedVariable={draggedVariable}
                                    onDragStart={handleDragStart}
                                    onDragOver={handleDragOver}
                                    onDropToShared={handleDropToShared}
                                    onDropToCustomer={handleDropToCustomer}
                                    onMoveToCustomer={moveToCustomer}
                                    onMoveToShared={moveToShared}
                                    onSharedValueChange={
                                        handleSharedValueChange
                                    }
                                    disableVariableMovement={
                                        isInternalFiltersMode
                                    }
                                />
                            </div>

                            {/* Test Email */}
                            <div className="p-4 border rounded-md border-orange-300 bg-orange-50/50 relative !mt-4">
                                <div className="flex items-center absolute top-0 left-2 -translate-y-1/2">
                                    <Typogaphy className="font-medium text-sm text-orange-600 capitalize bg-white px-2">
                                        {t('mailerCampaign:test_email')}
                                    </Typogaphy>
                                </div>
                                <div className="flex items-end gap-2 flex-wrap">
                                    <FormItem
                                        control={control}
                                        name="test_email"
                                        type="textfield"
                                        label={t('recipients')}
                                        placeholder={
                                            currentUserEmail ||
                                            t('mailerCampaign:enter_test_email')
                                        }
                                        className="flex-1 min-w-[250px]"
                                    />
                                    <Button
                                        variant="outlined"
                                        type="button"
                                        onClick={handleSendTest}
                                        startIcon={<SendIcons />}
                                        disabled={
                                            !watchedValues.test_email ||
                                            isSendingTest ||
                                            !campaign
                                        }
                                    >
                                        Test
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        type="button"
                                        startIcon={<DownLoadIcons />}
                                        onClick={handleDownloadEml}
                                        disabled={
                                            !watchedValues.template_content
                                        }
                                    >
                                        {t('mailerCampaign:download_eml')}
                                    </Button>
                                </div>
                            </div>

                            {/* Receivers */}
                            <ReceiversSection
                                control={control}
                                sourceType={
                                    watchedValues.mailer_campaign_source_type
                                }
                                selectedSource={selectedSource}
                                receivers={receivers}
                                receiverVariables={receiverVariables}
                                isNew={isNew}
                                filtersPreview={filtersPreview}
                                excludedCustomerIds={
                                    watchedValues.mailer_campaign_filters
                                        .excluded_customer_ids || []
                                }
                                filtersPage={filtersPage}
                                filtersPageSize={filtersPageSize}
                                filtersTotalWithoutSearch={
                                    filtersTotalWithoutSearch
                                }
                                isPreviewingFilters={isPreviewingFilters}
                                onReceiversChange={handleReceiversChange}
                                onSourceChange={handleSourceChange}
                                onInternalModeChange={handleInternalModeChange}
                                onPreviewFilters={handlePreviewFilters}
                                onFiltersPageChange={handleFiltersPageChange}
                                onToggleExcludeCustomer={
                                    handleToggleExcludeCustomer
                                }
                            />

                            {/* SMTP Settings */}
                            <SmtpSettings
                                control={control}
                                smtpEnabled={watchedValues.smtp_enabled}
                                onTestStatusChange={setSmtpTested}
                            />
                            <SenderSettings
                                control={control}
                                senderEnabled={watchedValues.sender_enabled}
                            />
                        </form>
                    </FormProvider>
                </div>
            ) : (
                /* Delivery History Tab */
                <div className="bg-white rounded-md p-4 shadow-sm border">
                    {campaign && <DeliveryProgress campaign={campaign} />}
                </div>
            )}

            {/* Fixed Bottom Action Bar - only show on config tab */}
            {activeTab === 'config' && (
                <ActionBar
                    isNew={isNew}
                    receiversCount={receivers.length}
                    isSubmitting={isCreating || isUpdating}
                    campaignStatus={campaign?.mailer_campaign_status}
                    sourceType={watchedValues.mailer_campaign_source_type}
                    internalMode={watchedValues.mailer_campaign_internal_mode}
                    filtersPreviewTotal={filtersTotalWithoutSearch}
                    excludedCount={
                        watchedValues.mailer_campaign_filters
                            .excluded_customer_ids?.length || 0
                    }
                    sharedVariables={sharedVariablesWithValues}
                    smtpEnabled={watchedValues.smtp_enabled}
                    smtpTested={smtpTested}
                    isStartingDelivery={isStartingDelivery}
                    onCancel={() => navigate(paths.mailerCampaign)}
                    onSubmit={handleSubmit((data) =>
                        onSubmit(
                            data,
                            {
                                customerVariables: receiverVariables,
                                sharedVariables: sharedVariablesWithValues,
                            },
                            attachments
                        )
                    )}
                    onBulkDelivery={() =>
                        handleBulkDelivery(() => setActiveTab('history'))
                    }
                />
            )}
        </div>
    )
}

export default Detail
